import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { t, getal, wanneer, geleden } from '../i18n.js';
import { St, nessusErnst } from '../components/Status.jsx';
import Stapelstaven from '../components/Stapelstaven.jsx';
import Lijngrafiek from '../components/Lijngrafiek.jsx';
import Periodes from '../components/Periodes.jsx';

/**
 * Scans van Nessus (docs/besluiten/nessus-koppeling.md): per klant de gescande publieke adressen, met hun open
 * poorten en kwetsbaarheden. Een adres hoort bij een klant via de IP-reeksen onder Beheer › Klanten.
 */
export default function Scans({ klant, mij }) {
  return (
    <>
      <div className="page-head"><div>
        <h1>{t('Scans')}</h1>
        <p className="sub">{t('Publieke adressen van klanten zoals Nessus ze ziet: open poorten en de kwetsbaarheden per poort.')}</p>
      </div></div>
      <Adressen klant={klant} magBeheren={mij.permissions.includes('rechten.beheren')} />
    </>
  );
}

function Adressen({ klant, magBeheren }) {
  const [filter, zetFilter] = useState('alle');
  const q = useQuery({ queryKey: ['scans', klant], queryFn: () => api.scans(klant), refetchInterval: 60_000 });
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const d = q.data.data;
  if (!d.linked && !d.hosts.length) {
    return <section className="card"><p className="empty">{magBeheren
      ? t('Er is nog geen koppeling met Nessus. Stel hem in onder Beheer › Nessus.') : t('Er is nog geen koppeling met Nessus.')}</p></section>;
  }
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{n}</div><div className="t">{label}</div></div>;
  const lijst = d.hosts.filter((h) => (filter === 'alle' ? true : filter === 'kwetsbaar' ? h.findings.critical + h.findings.high > 0
    : filter === 'veranderd' ? h.changes && (h.changes.newPorts || h.changes.closedPorts) : true));
  return (
    <>
      {d.syncError && <div className="callout warn" style={{ marginBottom: 14 }}>{t('Het laatste inlezen uit Nessus ging mis:')} {t(d.syncError)}</div>}
      <Verloop klant={klant} />
      <div className="tiles">
        {tegel(getal(d.totals.hosts), t('adressen'))}
        {tegel(getal(d.totals.openPorts), t('open poorten'))}
        {tegel(getal(d.totals.critical), t('kritiek'), d.totals.critical ? 'warn' : '')}
        {tegel(getal(d.totals.high), t('hoog'), d.totals.high ? 'warn' : '')}
      </div>
      <div className="filters">
        <button type="button" className="chip" aria-pressed={filter === 'alle'} onClick={() => zetFilter('alle')}>{t('Alle adressen')}</button>
        <button type="button" className="chip" aria-pressed={filter === 'kwetsbaar'} onClick={() => zetFilter('kwetsbaar')}>{t('Kritiek of hoog')}</button>
        <button type="button" className="chip" aria-pressed={filter === 'veranderd'} onClick={() => zetFilter('veranderd')}>{t('Poorten veranderd')}</button>
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 13 }}>{d.syncedAt ? t('Uit Nessus ingelezen {wanneer}', { wanneer: geleden(d.syncedAt) }) : ''}</span>
      </div>
      <section className="card">
        {lijst.length ? (
          <table>
            <thead><tr><th>{t('Adres')}</th>{klant === 'alle' && <th className="hide-sm">{t('Klant')}</th>}<th>{t('Open poorten')}</th><th>{t('Kwetsbaarheden')}</th><th className="hide-sm">{t('Gescand')}</th></tr></thead>
            <tbody>{lijst.map((h) => (
              <tr key={h.id}>
                <td><Link to={`/scans/${h.id}`} className="mono"><b>{h.ip}</b></Link>{h.hostname && <><br /><span className="muted" style={{ fontSize: 12.5 }}>{h.hostname}</span></>}</td>
                {klant === 'alle' && <td className="hide-sm">{h.tenant.name}</td>}
                <td className="mono">{getal(h.openPorts)}{h.changes && (h.changes.newPorts > 0 || h.changes.closedPorts > 0) && (
                  <><br /><span className="muted" style={{ fontSize: 12 }}>{[h.changes.newPorts ? t('+{n} nieuw', { n: h.changes.newPorts }) : null,
                    h.changes.closedPorts ? t('−{n} dicht', { n: h.changes.closedPorts }) : null].filter(Boolean).join(' · ')}</span></>)}</td>
                <td><span className="bevindingen">
                  {['critical', 'high', 'medium', 'low'].filter((e) => h.findings[e]).map((e) => <St key={e} soort={nessusErnst[e].soort}>{getal(h.findings[e])} {t(nessusErnst[e].label).toLowerCase()}</St>)}
                  {!['critical', 'high', 'medium', 'low'].some((e) => h.findings[e]) && <St soort="good">{t('geen')}</St>}
                  {h.exploitable > 0 && <St soort="critical">{t('{n} met exploit', { n: h.exploitable })}</St>}
                </span></td>
                <td className="hide-sm muted">{h.scannedAt ? wanneer(h.scannedAt) : '—'}<br /><span style={{ fontSize: 12 }}>{h.scan}</span></td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p className="empty">{d.hosts.length ? t('Geen adressen met dit filter.') : t('Nog geen gescande adressen bij deze klant. Staan de IP-reeksen van de klant onder Beheer › Klanten?')}</p>}
      </section>
    </>
  );
}

/**
 * Door de tijd (docs/besluiten/meer-grafieken.md): per run van een scan de bevindingen naar ernst (de kleuren van
 * Nessus-ernst, met het woord in legenda en tooltip) en de open poorten. Gaat het de goede kant op?
 */
function Verloop({ klant }) {
  const [dagen, zetDagen] = useState(180);
  const q = useQuery({ queryKey: ['grafiek', 'scans', klant, dagen], queryFn: () => api.grafiek('scans', klant, dagen), refetchInterval: 60_000 });
  const runs = q.data?.data.runs ?? [];
  const kleur = { critical: 'var(--st-critical)', high: 'var(--st-serious)', medium: 'var(--st-warning)', low: 'var(--ink-mute)' };
  const reeksen = ['low', 'medium', 'high', 'critical'].map((e) => ({ sleutel: e, label: t(nessusErnst[e].label), kleur: kleur[e] }));
  const kop = (p) => `${p.name} · ${new Date(p.van).toLocaleDateString('nl-NL', { timeZone: 'Europe/Amsterdam' })}`;
  return (
    <>
      <div className="group-h"><h2 style={{ margin: 0, fontSize: 16 }}>{t('Door de tijd')}</h2>
        <Periodes keuzes={[90, 180, 365]} waarde={dagen} onKies={zetDagen} /></div>
      {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : runs.length < 2 ? (
        <section className="card" style={{ marginBottom: 18 }}><p className="empty">{t('Nog te weinig scans in deze periode voor een verloop.')}</p></section>
      ) : (
        <div className="grid-2" style={{ marginBottom: 18 }}>
          <section className="card"><header><h2>{t('Bevindingen per scan')}</h2>
            <span className="meta legenda">{reeksen.slice().reverse().map((r) => <span key={r.sleutel}><i style={{ background: r.kleur }} />{r.label}</span>)}</span></header>
            <Stapelstaven punten={runs} reeksen={reeksen} kop={kop} label={t('Bevindingen per scan')} /></section>
          <section className="card"><header><h2>{t('Open poorten per scan')}</h2></header>
            <Lijngrafiek punten={runs.map((r) => ({ van: r.van, n: r.poorten }))} perDag eenheid={t('open poorten')} label={t('Open poorten per scan')} /></section>
        </div>
      )}
    </>
  );
}
